import { useEffect, useState } from "react"
import { deleteApi, getApi, postApi } from "../../../../utils/services";
import { TIMESHEET_IMPORT, UPLOAD_MULTI_LINE_TIMESHEET, UPLOAD_SINGLE_LINE_TIMESHEET } from "../../../../utils/services/apiPath";
import { useParams } from "react-router-dom";
import { ReactSpreadsheetImport, StepType } from "react-spreadsheet-import";
import useColumn from "../../../../utils/customHooks/useColumns";
import { Button } from "@mui/material";
import { UploadFile } from "@mui/icons-material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { ImportGridCustomToolbar } from "./AvailablePayrollImports";
import { useSelector } from "react-redux";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { R_PROJECTS } from "../../../../utils/permissionReferenceConfig";


const singleLineHeaders =
  [
    "firstName",
    "lastName",
    "totalOverTimeHours",
    "totalStandardHours",
    "weekEndingDate",
    "sunday",
    "saturday",
    "friday",
    "thursday",
    "wednesday",
    "tuesday",
    "monday"
  ]

const singleLineFields = singleLineHeaders.map(fieldName => ({
  label: fieldName,
  key: fieldName,
  fieldType: {
    type: "input"
  }
}));

const multiLineHeaders = [
  "lastName",
  "firstName",
  "workedDate",
  "totalStandardHours",
  "weekEndingDate"
]

const multiLineFields = multiLineHeaders.map(fieldName => ({
  label: fieldName,
  key: fieldName,
  fieldType: {
    type: "input"
  }
}));


const UploadActionCell = ({ onClick }) => {
  return (
    <div>
      <Button variant="contained" startIcon={<UploadFile />} onClick={onClick}>Import</Button>
    </div>
  )
}

const AvailableTimesheetImports = () => {
  const [availableTimesheets, setAvailableTimesheets] = useState([])
  const [selectedTimesheet, setSelectedTimesheet] = useState([])
  const [isMultiLineModal, setIsMultiLineModal] = useState(false)
  const [isMultiLineOpen, setIsMultiLineOpen] = useState(false)
  const [isSingleLineOpen, setIsSingleLineOpen] = useState(false)
  const [deleteImportId, setDeleteImportId] = useState("")
  const { id } = useParams();
  const { canExport, canImport } = useSelector(subModPermissionSelector(R_PROJECTS, "Available Payroll Imports"));


  const handleClick = (importId) => {
    const isMultiLine = getSelectedTimesheet(importId)
    setDeleteImportId(importId)
  }

  const columns = [
    {
      field: "fileName",
      headerName: "File Name",
      flex: 1,
    },
    {
      field: "clientImportId",
      headerName: "Actions",
      renderCell: params => <UploadActionCell params={params} onClick={() => handleClick(params.row.clientImportId)} />,
      width: 150,
    },
  ].filter(col => (col.field === "clientImportId" ? canImport : true))


  function stringifyValuesInArray(arr) {
    if (!Array.isArray(arr)) {
      return arr;
    }

    return arr.map(obj => stringifyValues(obj));
  }

  function stringifyValues(obj) {
    if (typeof obj !== 'object' || obj === null) {
      return String(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map(item => stringifyValues(item));
    }

    const newObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        newObj[key] = stringifyValues(obj[key]);

      }
    }
    return newObj;
  }

  const onSubmit = async (data, file) => {
    try {
      let url = isMultiLineModal ? UPLOAD_MULTI_LINE_TIMESHEET : UPLOAD_SINGLE_LINE_TIMESHEET
      url = url.replace('{projectId}', id)
      const response = await postApi(url, {
        fileModel: {
          base64String: "",
          fileName: ""
        },
        data: data?.validData
      })
      if (response?.error) {
        throw new Error()
      } else {
        deleteTimesheet()
      }
    } catch (e) {
      return errorToast("Failed to import file")
    }
    successToast("Successfuly imported timesheet")
  }

  const deleteTimesheet = async () => {
    let url = TIMESHEET_IMPORT.replace("{projectId}", id)
    try {
      await deleteApi(`${url}/${deleteImportId}`)
      setSelectedTimesheet([])
      getAvailableTimesheets()
    } catch (e) {
      errorToast("Unable to mark timesheet as completed")
    }
  }


  const getAvailableTimesheets = async () => {
    const url = TIMESHEET_IMPORT.replace("{projectId}", id)
    const { processedFiles } = await getApi(url)
    setAvailableTimesheets(processedFiles)
  }

  const getSelectedTimesheet = async (importId) => {
    let url = TIMESHEET_IMPORT.replace("{projectId}", id)
    const { data, isMultiLine } = await getApi(`${url}/processed/${importId}`)
    if (data) {
      const values = data.map(d => Object.values(d))
      const processed = stringifyValuesInArray(values)
      setIsMultiLineModal(isMultiLine == "true")
      setIsMultiLineOpen(isMultiLine == "true")
      setIsSingleLineOpen(isMultiLine == "false")
      setSelectedTimesheet(processed)
    }
    return isMultiLine
  }

  const onClose = () => {
    setIsSingleLineOpen(false)
    setIsMultiLineOpen(false)
  }

  useEffect(() => {
    if (id) {
      getAvailableTimesheets()
    }
  }, [])


  return (
    <div className="rs-grid-wrapper v2" style={{ display: "flex", flexDirection: "column" }}>
      <DataGridPro
        classes={{
          cell: "proGrid-table-cell"
        }}
        sortingOrder={["desc", "asc"]}
        columnHeaderHeight={44}
        rowHeight={44}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10
            }
          }
        }}
        slots={{
          toolbar: () => <ImportGridCustomToolbar canExport={canExport} />,
          // noResultsOverlay: NoResultsOverlay
        }}
        slotProps={{ toolbar: { showQuickFilter: true } }}
        rows={availableTimesheets}
        getRowId={row => row.clientImportId}
        pagination
      // groupingColDef={groupingColDef}
      />
      {(selectedTimesheet.length > 0 && !isMultiLineModal) &&
        <ReactSpreadsheetImport
          isOpen={isSingleLineOpen}
          onClose={onClose}
          fields={singleLineFields}
          initialStepState={{
            type: StepType.matchColumns,
            data: selectedTimesheet,
            headerValues: singleLineHeaders
          }}
          customTheme={{
            rsi: {
              overflow: "auto !important"
            }
          }}
          onSubmit={onSubmit}
        />
      }
      {(selectedTimesheet.length > 0 && isMultiLineModal) &&
        <ReactSpreadsheetImport
          isOpen={isMultiLineOpen}
          onClose={onClose}
          fields={multiLineFields}
          initialStepState={{
            type: StepType.matchColumns,
            data: selectedTimesheet,
            headerValues: multiLineHeaders
          }}
          customTheme={{
            rsi: {
              overflow: "auto !important"
            }
          }}
          onSubmit={onSubmit}
        />
      }
    </div>
  )
}

export default AvailableTimesheetImports